import { Button, Form, Dropdown } from "react-bootstrap";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { FaTimesCircle, FaCheck, FaEdit } from "react-icons/fa";
import { useParams } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import SplitPane from "react-split-pane";
import { useIsMobile } from "../../hooks/UseWindowSize";
import { RichTextEditor } from "../../components/richText/RichTextEditor.js";
import { uploadFile } from "../../upload/uploadFile.jsx";
import { useUser } from "../../contexts/UserContext.js";
import { dateOptions } from "../PSetSplash.js";
import {
  doc,
  getFirestore,
  setDoc,
  getDoc,
  serverTimestamp,
  collection,
  query,
  orderBy,
  getDocs,
} from "firebase/firestore";
import { VideoInput } from "./AboveAndBeyond/VideoInput.jsx";
import { FilesInput } from "./AboveAndBeyond/FilesInput.jsx";
import { LinkInput } from "./AboveAndBeyond/LinkInput.jsx";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";
import { Feedback } from "./Feedback.js";

export const AboveAndBeyond = ({ publicPsetData, allFeedback }) => {
  const { qtrId, psetId } = useParams();
  const isMobile = useIsMobile();
  const user = useUser();
  const userId = user?.uid;
  const db = getFirestore();
  const publicQuestionInfo = useMemo(
    () => publicPsetData?.questionInfo || [],
    [publicPsetData?.questionInfo]
  );

  // due date is 3 days after pset due date
  const ABDueDate = useMemo(() => {
    if (!publicPsetData?.due) return null;
    const base = new Date(publicPsetData.due);
    if (isNaN(base.getTime())) return null;
    return new Date(base.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString();
  }, [publicPsetData?.due]);

  const ABDueDateFormatted = useMemo(() => {
    if (!ABDueDate) return "Unknown";
    return new Date(ABDueDate).toLocaleString("en-US", dateOptions);
  }, [ABDueDate]);

  const pastDue = useMemo(() => {
    if (!ABDueDate) return false;
    const today = new Date();
    return today.getTime() > new Date(ABDueDate).getTime();
  }, [ABDueDate]);

  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [submittedProjectId, setSubmittedProjectId] = useState(null);
  const [activeTab, setActiveTab] = useState("details");

  // there's a bug where the tab content isn't scrollable.
  // we set a container ref to dynamically measure the height of the tab content and ensure contents are visible and scrollable
  const tabContainerRef = useRef(null);
  const [tabContentHeight, setTabContentHeight] = useState(null);

  useEffect(() => {
    const container = tabContainerRef.current;
    if (!container) return;

    const updateHeight = () => {
      const containerHeight = container.clientHeight;
      // Subtract approximate height of tab navigation (~42px)
      const availableHeight = containerHeight - 50;
      setTabContentHeight(availableHeight > 100 ? availableHeight : null);
    };

    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [editingProjectId]);

  // note: description is directly saved to Firestore with RichTextEditor
  const [projectForm, setProjectForm] = useState({
    id: null,
    name: "",
    specificProject: false,
    selectedQuestion: "",
    videoType: "link",
    videoLink: "",
    videoFile: null,
    files: [],
    links: [],
  });
  const [uploadState, setUploadState] = useState({
    videoProgress: 0,
    videoError: null,
    fileProgress: {},
    fileError: {},
  });
  const [validationErrors, setValidationErrors] = useState({
    video: null,
    name: null,
    question: null,
    description: null,
  });
  const [isSaving, setIsSaving] = useState(false);

  const updateFormField = (field, value) =>
    setProjectForm((prev) => ({ ...prev, [field]: value }));

  // helper to format date
  const formatDate = (dateValue) => {
    if (!dateValue) return "Unknown";

    const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);

    return isNaN(date.getTime()) ? "Unknown" : date.toLocaleString();
  };

  const getProjectPath = (projectId) =>
    `users/${userId}/${qtrId}/${psetId}/projects/${projectId}`;

  const answerPath = useMemo(
    () => `users/${userId}/${qtrId}/${psetId}/answers/aboveAndBeyond`,
    [userId, qtrId, psetId]
  );

  // helper to get the currently editing project
  const editingProject = useMemo(() => {
    if (!editingProjectId) return null;
    return projects.find((p) => p.id === editingProjectId) || null;
  }, [projects, editingProjectId]);

  // load projects from Firestore; use useCallback to avoid re-creating the function on every render
  const loadProjects = useCallback(async () => {
    try {
      setProjectsLoading(true);

      // load current submitted project ID from answers doc
      const answerDoc = await getDoc(doc(db, answerPath));
      if (answerDoc.exists()) {
        setSubmittedProjectId(answerDoc.data()?.submittedProjectId || null);
      }

      // all projects
      const projectsRef = collection(
        db,
        `users/${userId}/${qtrId}/${psetId}/projects`
      );
      const q = query(projectsRef, orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      const projectsList = [];
      querySnapshot.forEach((doc) => {
        const projectData = { id: doc.id, ...doc.data() };
        projectsList.push(projectData);
      });

      setProjects(projectsList);
    } catch (e) {
      console.error("Failed to load projects:", e);
    } finally {
      setProjectsLoading(false);
    }
  }, [userId, qtrId, psetId, db, answerPath]);

  // call loadProjects when userId, qtrId, or psetId changes
  useEffect(() => {
    if (!userId || !qtrId || !psetId) {
      setProjectsLoading(false);
      return;
    }
    loadProjects();
  }, [userId, qtrId, psetId, db, loadProjects]);

  // handles one or the other logic for video description input type
  const handleVideoFileSelect = (e) => {
    const file = e.target.files[0];
    if (file?.type.startsWith("video/")) {
      updateFormField("videoFile", file);
      updateFormField("videoLink", "");
      setUploadState((prev) => ({ ...prev, videoError: null }));
    } else {
      setUploadState((prev) => ({
        ...prev,
        videoError: "Please select a valid video file",
      }));
    }
    e.target.value = "";
  };

  const handleFileUpload = (e) => {
    updateFormField("files", [
      ...projectForm.files,
      ...Array.from(e.target.files),
    ]);
    e.target.value = "";
  };

  const handleRemoveFile = (index) =>
    updateFormField(
      "files",
      projectForm.files.filter((_, i) => i !== index)
    );

  const resetForm = () => {
    setProjectForm({
      id: null,
      name: "",
      specificProject: false,
      selectedQuestion: "",
      videoType: "link",
      videoLink: "",
      videoFile: null,
      files: [],
      links: [],
    });
    setUploadState({
      videoProgress: 0,
      videoError: null,
      fileProgress: {},
      fileError: {},
    });
    setValidationErrors({
      video: null,
      name: null,
      question: null,
      description: null,
    });
  };

  const handleOpenProjectModal = () => {
    resetForm();
    updateFormField("id", Date.now().toString());
    setShowProjectModal(true);
  };

  const handleCloseProjectModal = () => {
    setShowProjectModal(false);
    setEditingProjectId(null);
    resetForm();
  };

  // ensure required fields are filled
  const validateProject = async (proj) => {
    const existingVideoUrl = proj?.existingVideoUrl;
    const errors = {};
    if (!projectForm.name.trim()) errors.name = "Project name is required";
    if (projectForm.specificProject && !projectForm.selectedQuestion) {
      errors.question = "Please select a question";
    }
    const hasExistingVideo = Boolean(
      typeof existingVideoUrl === "string"
        ? existingVideoUrl.trim()
        : existingVideoUrl
    );
    if (projectForm.videoType === "link" && !projectForm.videoLink.trim()) {
      errors.video = "Video link is required";
    } else if (
      projectForm.videoType === "file" &&
      !projectForm.videoFile &&
      !hasExistingVideo
    ) {
      errors.video = "Video file is required";
    }

    // description needs to be validated by reading the project doc since it's directly saved to Firestore
    // if the read fails OR the field is missing, show error.
    let hasDescription = false;
    if (projectForm.id && userId) {
      try {
        const projectDoc = await getDoc(
          doc(db, getProjectPath(projectForm.id))
        );
        hasDescription = Boolean(projectDoc.data()?.description);
      } catch {
        hasDescription = false;
      }
    }
    if (!hasDescription) errors.description = "Description is required";

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // helper to upload file
  const uploadFileAsync = (file, dir, onProgress) =>
    new Promise((resolve, reject) => {
      uploadFile(
        file,
        dir,
        (url, fileName, fileId) => resolve({ url, fileName, fileId }),
        onProgress,
        (error) => reject(error)
      );
    });

  // upload videos and files
  const uploadProjectFiles = async (
    projectId,
    existingVideoUrl = null,
    existingVideoFileName = null
  ) => {
    let finalVideoUrl = null;
    let finalVideoFileName = null;

    // video
    if (projectForm.videoType === "link") {
      finalVideoUrl = projectForm.videoLink.trim();
    } else if (projectForm.videoType === "file") {
      if (projectForm.videoFile) {
        setUploadState((prev) => ({
          ...prev,
          videoProgress: 0,
          videoError: null,
        }));

        try {
          const { url, fileName } = await uploadFileAsync(
            projectForm.videoFile,
            `projects/${userId}/${qtrId}/${psetId}/${projectId}/video`,
            (progress) =>
              setUploadState((prev) => ({ ...prev, videoProgress: progress }))
          );

          finalVideoUrl = url;
          finalVideoFileName = fileName;
        } catch (err) {
          setUploadState((prev) => ({
            ...prev,
            videoError: err?.message || "Failed to upload video",
          }));
          throw err;
        }
      } else if (existingVideoUrl) {
        finalVideoUrl = existingVideoUrl;
        finalVideoFileName = existingVideoFileName;
      }
    }

    // files
    const storageDir = `projects/${userId}/${qtrId}/${psetId}/${projectId}/files`;

    const uploadedFiles = await Promise.all(
      projectForm.files.map(async (file, index) => {
        if (!(file instanceof File)) return file;

        const progressKey = `file_${index}`;
        setUploadState((prev) => ({
          ...prev,
          fileProgress: { ...prev.fileProgress, [progressKey]: 0 },
        }));

        try {
          const { url, fileName, fileId } = await uploadFileAsync(
            file,
            storageDir,
            (progress) =>
              setUploadState((prev) => ({
                ...prev,
                fileProgress: { ...prev.fileProgress, [progressKey]: progress },
              }))
          );

          setUploadState((prev) => {
            const { [progressKey]: _, ...rest } = prev.fileProgress;
            return { ...prev, fileProgress: rest };
          });

          return { name: fileName, url, fileId };
        } catch (err) {
          setUploadState((prev) => ({
            ...prev,
            fileError: {
              ...prev.fileError,
              [progressKey]: err?.message || "Failed to upload file",
            },
          }));
          throw err;
        }
      })
    );

    return { finalVideoUrl, finalVideoFileName, uploadedFiles };
  };

  // build final project data object to save
  const buildProjectData = (
    finalVideoUrl,
    uploadedFiles,
    isNew = false,
    finalVideoFileName = null
  ) => ({
    name: projectForm.name.trim(),
    ...(isNew && {
      // for new projects, set specific project and question
      specificProject: projectForm.specificProject,
      selectedQuestion: projectForm.specificProject
        ? projectForm.selectedQuestion
        : "",
      createdAt: serverTimestamp(),
    }),
    videoUrl: finalVideoUrl,
    videoType: projectForm.videoType,
    ...(finalVideoFileName && { videoFileName: finalVideoFileName }),
    files: uploadedFiles,
    submissionLinks: projectForm.links.filter((link) => link.trim()),
    updatedAt: serverTimestamp(),
  });

  // submit a project (write reference to answers path for grading)
  const handleSubmitProject = async (projectId, projectData = null) => {
    if (!projectId || !userId) return;

    // Use provided projectData or find it in the array
    const project = projectData || projects.find((p) => p.id === projectId);
    if (!project) return;

    setIsSaving(true);
    try {
      // write reference to the answers path for grading compatibility
      await setDoc(
        doc(db, answerPath),
        {
          submittedProjectId: projectId,
          submittedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setSubmittedProjectId(projectId);

      alert("Project submitted successfully!");
    } catch (error) {
      console.error("Failed to submit project:", error);
      alert(`Failed to submit project: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // handle saving a project (new, edited, or submitted)
  const saveProject = async ({ projectId, isNew, submitAfter }) => {
    if (!projectId || !userId) return;

    const existing = isNew ? null : projects.find((p) => p.id === projectId);

    const validated = await validateProject({
      existingVideoUrl: existing?.videoUrl,
    });
    if (!validated) return;

    // confirm replacement if submitting
    if (submitAfter && submittedProjectId && submittedProjectId !== projectId) {
      const prevSub = projects.find((p) => p.id === submittedProjectId);
      const currentName = isNew ? projectForm.name : existing?.name;
      const confirmed = window.confirm(
        `"${currentName}" will replace "${
          prevSub?.name || "your current submission"
        }" as your submitted project. Continue?`
      );
      if (!confirmed) return;
    }

    setIsSaving(true);
    try {
      const { finalVideoUrl, finalVideoFileName, uploadedFiles } =
        await uploadProjectFiles(
          projectId,
          existing?.videoUrl,
          existing?.videoFileName
        );

      const data = buildProjectData(
        finalVideoUrl,
        uploadedFiles,
        isNew,
        finalVideoFileName
      );

      await setDoc(doc(db, getProjectPath(projectId)), data, { merge: true });

      // update local projects state
      setProjects((prev) => {
        const idx = prev.findIndex((p) => p.id === projectId);
        if (idx === -1) {
          return [
            {
              id: projectId,
              ...data,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            ...prev,
          ];
        }
        const copy = [...prev];
        copy[idx] = {
          ...copy[idx],
          ...data,
          updatedAt: new Date().toISOString(),
        };
        return copy;
      });

      if (submitAfter) {
        await handleSubmitProject(projectId, null);
      }
    } catch (error) {
      console.error("Failed to save project:", error);
      alert(`Failed to save project: ${error?.message || "Unknown error"}`);
    } finally {
      setIsSaving(false);
      setShowProjectModal(false);
    }
  };

  const handleSaveProject = () =>
    saveProject({ projectId: projectForm.id, isNew: true, submitAfter: false });

  const handleSaveEditedProject = () =>
    saveProject({
      projectId: editingProjectId,
      isNew: false,
      submitAfter: false,
    });

  const handleSaveAndSubmit = (isNew) =>
    saveProject({
      projectId: isNew ? projectForm.id : editingProjectId,
      isNew,
      submitAfter: true,
    });

  const handleOpenEditingPanel = (projectId) => {
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;
    setEditingProjectId(projectId);
    setActiveTab("details"); // Reset to details tab when switching projects
    setProjectForm({
      id: project.id,
      name: project.name,
      specificProject: project.specificProject || false,
      selectedQuestion: project.selectedQuestion || "",
      videoType: project.videoType || "link",
      videoLink: project.videoType === "link" ? project.videoUrl || "" : "",
      videoFile: null,
      files: project.files || [],
      links: project.submissionLinks || [],
    });
  };

  const handleCloseEditingPanel = () => {
    setEditingProjectId(null);
    resetForm();
  };

  return (
    <div
      className="container"
      style={{
        padding: "20px",
        height: "100vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <div>
        <h2>Above and Beyond</h2>
      </div>
      <div className="d-flex flex-column align-items-start">
        <div
          className="alert alert-primary mt-2"
          style={{ width: isMobile ? "100%" : "50%" }}
        >
          Welcome to Above and Beyond! Here you can submit a project
          (videos/links/files) that goes beyond the scope of the Pset. You can
          create as many projects as you like, but you can only submit one
          project per Pset. You can submit a project and continue editing it
          until {ABDueDateFormatted}.
        </div>
        <Button
          onClick={handleOpenProjectModal}
          disabled={!userId}
          style={{
            backgroundColor: "#002154",
            border: "none",
            marginBottom: "20px",
          }}
        >
          Create Project
        </Button>
        {!userId && <p>Please log in to create a project.</p>}
      </div>
      <SplitPane
        split={isMobile ? "horizontal" : "vertical"}
        size={isMobile ? "40%" : "50%"}
        minSize={isMobile ? 200 : 300}
        style={{
          flex: 1,
          overflow: "hidden",
          height: isMobile ? "80vh" : "100%",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
        }}
        resizerStyle={{ display: "none" }}
      >
        {/* Projects Table */}
        <div
          className="bordered d-flex flex-column"
          style={{
            marginRight: isMobile ? "0" : "10px",
            overflow: "auto",
          }}
        >
          <h4>Projects</h4>
          <table className="table table-hover">
            <thead>
              <tr>
                <th>Name</th>
                <th>Submitted</th>
                <th>Updated</th>
                <th>Edit</th>
              </tr>
            </thead>
            <tbody>
              {projectsLoading ? (
                <tr>
                  <td colSpan={4} className="text-center">
                    Loading projects...
                  </td>
                </tr>
              ) : !userId ? (
                <tr>
                  <td colSpan={4} className="text-center">
                    Please log in to view your projects.
                  </td>
                </tr>
              ) : projects.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center">
                    No projects yet. Click "Create Project" to get started.
                  </td>
                </tr>
              ) : (
                projects.map((project) => (
                  <tr
                    key={project.id}
                    onClick={() => handleOpenEditingPanel(project.id)}
                    style={{
                      cursor: "pointer",
                      backgroundColor:
                        editingProjectId === project.id
                          ? "#f0f7ff"
                          : "transparent",
                    }}
                  >
                    <td className="text-truncate">{project.name}</td>
                    <td>
                      {submittedProjectId === project.id ? (
                        <span className="text-success">
                          <FaCheck />
                        </span>
                      ) : (
                        <></>
                      )}
                    </td>
                    <td>{formatDate(project.updatedAt)}</td>
                    <td>
                      <button
                        style={{
                          backgroundColor: "transparent",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        <FaEdit size={20} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Project Details and Feedback*/}
        {editingProjectId ? (
          <div className="position-relative h-100">
            {isSaving && (
              // block the user from making changes while uploading files
              <div
                className="position-absolute top-0 start-0 end-0 bottom-0 bg-white bg-opacity-75 d-flex align-items-center justify-content-center"
                style={{ zIndex: 1000 }}
              >
                <span className="text-muted">Saving changes...</span>
              </div>
            )}
            <div
              ref={tabContainerRef}
              className="bordered rounded"
              style={{
                opacity: isSaving ? 0.5 : 1,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                position: "relative",
              }}
            >
              <Button
                variant="link"
                onClick={handleCloseEditingPanel}
                className="p-0 text-secondary"
                style={{
                  position: "absolute",
                  top: "8px",
                  right: "10px",
                  zIndex: 10,
                }}
              >
                <FaTimesCircle size={20} />
              </Button>
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k || "details")}
                id="projectDetailsTabs"
                key={`tabs-${editingProjectId}`}
              >
                <Tab
                  eventKey="details"
                  title={<span style={{ color: "black" }}>Details</span>}
                >
                  <div
                    style={{
                      maxHeight: tabContentHeight
                        ? `${tabContentHeight}px`
                        : undefined,
                      display: "flex",
                      flexDirection: "column",
                      padding: "10px",
                      overflow: "hidden",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-3 mt-2">
                      <div className="flex-grow-1">
                        <Form.Control
                          type="text"
                          value={projectForm.name}
                          onChange={(e) =>
                            updateFormField("name", e.target.value)
                          }
                          placeholder="Project name"
                          className={`fs-5 fw-semibold mb-0 ${
                            validationErrors.name ? "is-invalid" : ""
                          }`}
                          style={{ maxWidth: "70%" }}
                        />
                        {validationErrors.name && (
                          <p className="text-danger small mt-1 mb-0">
                            {validationErrors.name}
                          </p>
                        )}
                      </div>
                    </div>
                    <div
                      className="overflow-auto flex-grow-1 d-flex flex-column gap-3"
                      style={{ minHeight: 0 }}
                    >
                      <div
                        className={`d-flex ${
                          isMobile ? "flex-column gap-1" : "flex-row gap-3"
                        }`}
                      >
                        {projectForm.specificProject && (
                          <div className="d-flex flex-row gap-1">
                            <p className="fw-semibold mb-0">
                              Attached Question:{" "}
                            </p>
                            <p className="mb-0">
                              {publicQuestionInfo.find(
                                (q) => q.qId === projectForm.selectedQuestion
                              )?.title || "Untitled Question"}
                            </p>
                          </div>
                        )}
                        <div className="d-flex flex-row gap-1">
                          <p className="fw-semibold mb-0">Last updated: </p>
                          <p className="text-muted mb-0">
                            {formatDate(editingProject?.updatedAt)}
                          </p>
                        </div>
                      </div>

                      <VideoInput
                        videoType={projectForm.videoType}
                        videoLink={projectForm.videoLink}
                        videoFile={projectForm.videoFile}
                        existingVideo={
                          editingProject
                            ? {
                                url: editingProject.videoUrl,
                                type: editingProject.videoType,
                                name: editingProject.videoFileName,
                              }
                            : null
                        }
                        onVideoTypeChange={(type) => {
                          updateFormField("videoType", type);
                          if (type === "link") {
                            updateFormField("videoFile", null);
                          } else {
                            updateFormField("videoLink", "");
                          }
                          setUploadState((prev) => ({
                            ...prev,
                            videoError: null,
                          }));
                        }}
                        onVideoLinkChange={(link) => {
                          updateFormField("videoLink", link);
                          if (link.trim()) updateFormField("videoFile", null);
                          setValidationErrors((prev) => ({
                            ...prev,
                            video: null,
                          }));
                        }}
                        onVideoFileSelect={(e) => {
                          handleVideoFileSelect(e);
                          updateFormField("videoLink", "");
                        }}
                        validationError={validationErrors.video}
                        uploadProgress={uploadState.videoProgress}
                        uploadError={uploadState.videoError}
                        namePrefix="editing"
                      />
                      {userId && editingProjectId && (
                        <div>
                          <h6 className="d-flex align-items-center gap-2">
                            Description <span className="text-danger">*</span>
                          </h6>
                          <RichTextEditor
                            key={`description-edit-${editingProjectId}`}
                            user={userId}
                            editable={true}
                            firebaseDocPath={getProjectPath(editingProjectId)}
                            collaborative={false}
                            contentKey="description"
                            minHeight={100}
                          />
                          {validationErrors.description && (
                            <p className="text-danger small mt-1">
                              {validationErrors.description}
                            </p>
                          )}
                        </div>
                      )}
                      <div className={isMobile ? "" : "d-flex flex-row gap-3"}>
                        <FilesInput
                          files={projectForm.files}
                          onFileUpload={handleFileUpload}
                          onRemoveFile={handleRemoveFile}
                          namePrefix="editing"
                          style={{
                            flex: isMobile ? 0 : 1,
                            minWidth: 0,
                            marginBottom: isMobile ? "16px" : "",
                          }}
                        />
                        <LinkInput
                          links={projectForm.links}
                          onLinkAdd={(link) => {
                            updateFormField("links", [
                              ...projectForm.links,
                              link,
                            ]);
                          }}
                          onLinkChange={(index, link) => {
                            const updatedLinks = [...projectForm.links];
                            updatedLinks[index] = link;
                            updateFormField("links", updatedLinks);
                          }}
                          onLinkRemove={(index) => {
                            updateFormField(
                              "links",
                              projectForm.links.filter((_, i) => i !== index)
                            );
                          }}
                          namePrefix="editing"
                          style={{ flex: isMobile ? 0 : 1, minWidth: 0 }}
                        />
                      </div>
                      <div className="d-flex gap-2 justify-content-between mt-2">
                        <div className="d-flex gap-2">
                          <Button
                            variant="secondary"
                            onClick={handleCloseEditingPanel}
                            disabled={isSaving}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="primary"
                            onClick={handleSaveEditedProject}
                            disabled={
                              isSaving ||
                              (submittedProjectId === editingProjectId &&
                                pastDue)
                            }
                            style={{
                              backgroundColor: "#002154",
                              border: "none",
                            }}
                          >
                            {isSaving ? "Saving..." : "Save Changes"}
                          </Button>
                        </div>

                        <Button
                          variant="success"
                          onClick={() => handleSaveAndSubmit(false)}
                          disabled={
                            isSaving ||
                            submittedProjectId === editingProjectId
                            || pastDue
                          }
                          style={{ backgroundColor: "green", border: "none" }}
                        >
                          {isSaving ? "Submitting..." : "Submit Project"}
                        </Button>
                      </div>
                    </div>
                  </div>
                </Tab>

                {publicPsetData?.gradesReleased &&
                  allFeedback?.["above-and-beyond"] &&
                  editingProjectId === submittedProjectId && (
                    <Tab
                      eventKey="feedback"
                      title={<span style={{ color: "black" }}>Feedback</span>}
                    >
                      <div
                        style={{
                          maxHeight: tabContentHeight
                            ? `${tabContentHeight}px`
                            : undefined,
                          overflowY: "auto",
                          padding: "10px",
                        }}
                      >
                        <Feedback
                          key={`feedback-${editingProjectId}`}
                          user={user}
                          qtrId={qtrId}
                          psetId={psetId}
                          qId="above-and-beyond"
                          feedbackData={allFeedback?.["above-and-beyond"]}
                          publicPsetData={publicPsetData}
                        />
                      </div>
                    </Tab>
                  )}
              </Tabs>
            </div>
          </div>
        ) : (
          <div className="d-none" />
        )}
      </SplitPane>

      {/* Project Creation Modal */}
      <Modal
        show={showProjectModal}
        onHide={handleCloseProjectModal}
        centered
        animation={false}
        backdrop={true}
        keyboard={true}
        size="lg"
        onExited={() => {
          const backdrops = document.querySelectorAll(".modal-backdrop");
          backdrops.forEach((backdrop) => backdrop.remove());
          document.body.classList.remove("modal-open");
          document.body.style.paddingRight = "";
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Create Project</Modal.Title>
        </Modal.Header>
        <Modal.Body className="position-relative">
          {isSaving && (
            <div
              className="position-absolute top-0 start-0 end-0 bottom-0 bg-white bg-opacity-75 d-flex align-items-center justify-content-center"
              style={{ zIndex: 1000 }}
            >
              <span className="text-muted">Submitting project...</span>
            </div>
          )}
          <div
            className="d-flex flex-column gap-3 px-2"
            style={{
              opacity: isSaving ? 0.5 : 1,
              maxHeight: "70vh",
              overflowY: "auto",
            }}
          >
            {/* header */}
            <div className="d-flex flex-row gap-3">
              <div style={{ flex: 1 }}>
                <h6>
                  Project Name <span className="text-danger">*</span>
                </h6>
                <Form.Control
                  type="text"
                  value={projectForm.name}
                  onChange={(e) => updateFormField("name", e.target.value)}
                  placeholder="Project name"
                  className={validationErrors.name ? "is-invalid" : ""}
                />
                {validationErrors.name && (
                  <p className="text-danger small mt-1">
                    {validationErrors.name}
                  </p>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <Form.Check
                  label="Attach to Question"
                  name="projectType"
                  id="projectType-question"
                  checked={projectForm.specificProject}
                  className="mb-1"
                  onChange={() =>
                    updateFormField(
                      "specificProject",
                      !projectForm.specificProject
                    )
                  }
                />
                {projectForm.specificProject && (
                  <Form.Select
                    value={projectForm.selectedQuestion}
                    onChange={(e) =>
                      updateFormField("selectedQuestion", e.target.value)
                    }
                    className={validationErrors.question ? "is-invalid" : ""}
                  >
                    <option>Select a question</option>
                    {publicQuestionInfo?.map((question) => (
                      <option key={question.qId} value={question.qId}>
                        {question.title}
                      </option>
                    ))}
                  </Form.Select>
                )}
                {validationErrors.question && (
                  <p className="text-danger small mt-1">
                    {validationErrors.question}
                  </p>
                )}
              </div>
            </div>
            {/* project details + description */}
            <VideoInput
              videoType={projectForm.videoType}
              videoLink={projectForm.videoLink}
              videoFile={projectForm.videoFile}
              onVideoTypeChange={(type) => {
                updateFormField("videoType", type);
                if (type === "link") {
                  updateFormField("videoFile", null);
                } else {
                  updateFormField("videoLink", "");
                }
                setUploadState((prev) => ({ ...prev, videoError: null }));
              }}
              onVideoLinkChange={(link) => {
                updateFormField("videoLink", link);
                setValidationErrors((prev) => ({ ...prev, video: null }));
              }}
              onVideoFileSelect={handleVideoFileSelect}
              validationError={validationErrors.video}
              uploadProgress={uploadState.videoProgress}
              uploadError={uploadState.videoError}
              namePrefix="project"
            />
            <div>
              <h6>
                Description <span className="text-danger">*</span>
              </h6>
              <div>
                <RichTextEditor
                  key={`description-create-${projectForm.id}`}
                  user={userId}
                  editable={true}
                  firebaseDocPath={getProjectPath(projectForm.id)}
                  collaborative={false}
                  contentKey="description"
                  minHeight={100}
                />
                {validationErrors.description && (
                  <p className="text-danger small mt-1">
                    {validationErrors.description}
                  </p>
                )}
              </div>
            </div>
            <div className={isMobile ? "" : "d-flex flex-row gap-3"}>
              <FilesInput
                files={projectForm.files}
                onFileUpload={handleFileUpload}
                onRemoveFile={handleRemoveFile}
                namePrefix="project"
                style={{ flex: isMobile ? 0 : 1 }}
              />
              <LinkInput
                links={projectForm.links}
                onLinkAdd={(link) => {
                  updateFormField("links", [...projectForm.links, link]);
                }}
                onLinkChange={(index, link) => {
                  const updatedLinks = [...projectForm.links];
                  updatedLinks[index] = link;
                  updateFormField("links", updatedLinks);
                }}
                onLinkRemove={(index) => {
                  updateFormField(
                    "links",
                    projectForm.links.filter((_, i) => i !== index)
                  );
                }}
                namePrefix="project"
                style={{ flex: isMobile ? 0 : 1 }}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseProjectModal}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Dropdown>
            <Dropdown.Toggle
              variant="primary"
              disabled={!userId}
              style={{ backgroundColor: "#002154", border: "none" }}
            >
              Create Project
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() => handleSaveAndSubmit(true)}
                disabled={pastDue}
              >
                Submit Project
              </Dropdown.Item>
              <Dropdown.Item onClick={handleSaveProject}>
                Create Project as Draft
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
